import { useState, useEffect } from 'react';
import { cn } from '../../lib/cn';

/**
 * DynamicIsland - A floating status pill that shows animated progress messages
 * Similar to Apple's Dynamic Island, appears at the top center of the screen
 *
 * @param {boolean} isActive - Whether the island is visible
 * @param {string[]} steps - Array of status messages to cycle through
 * @param {number} stepDuration - Duration to show each step in ms (default: 1200)
 */
export default function DynamicIsland({
  isActive,
  steps = ['Processing...'],
  stepDuration = 1200
}) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  // Handle visibility transitions
  useEffect(() => {
    if (isActive) {
      setIsExiting(false);
      setCurrentStepIndex(0);
      // Small delay before showing for smooth entrance
      const showTimer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(showTimer);
    } else if (isVisible) {
      // Exit animation
      setIsExiting(true);
      const hideTimer = setTimeout(() => {
        setIsVisible(false);
        setIsExiting(false);
      }, 300);
      return () => clearTimeout(hideTimer);
    }
  }, [isActive, isVisible]);

  // Cycle through steps while active
  useEffect(() => {
    if (!isActive || steps.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentStepIndex(prev => (prev + 1) % steps.length);
    }, stepDuration);

    return () => clearInterval(interval);
  }, [isActive, steps.length, stepDuration]);

  if (!isVisible && !isActive) return null;

  const currentStep = steps[currentStepIndex];

  return (
    <div
      className={cn(
        "fixed top-4 left-1/2 -translate-x-1/2 z-50",
        "transition-all duration-300 ease-out",
        isExiting ? "opacity-0 -translate-y-2 scale-95" : "opacity-100 translate-y-0 scale-100",
        !isVisible && !isExiting && "opacity-0 -translate-y-4 scale-90"
      )}
    >
      <div
        className={cn(
          "flex items-center gap-2.5 px-4 py-2.5",
          "bg-gray-900 text-white rounded-full",
          "dark:bg-white dark:text-gray-900",
          "shadow-lg shadow-gray-900/25 dark:shadow-gray-300/25",
          "border border-gray-700/50 dark:border-gray-200/50"
        )}
      >
        {/* Animated spinner icon */}
        <div className="relative w-4 h-4 flex-shrink-0">
          {/* Outer spinning ring */}
          <svg
            className="w-4 h-4 animate-spin"
            viewBox="0 0 16 16"
            fill="none"
          >
            <circle
              cx="8"
              cy="8"
              r="6"
              stroke="currentColor"
              strokeOpacity="0.25"
              strokeWidth="2"
            />
            <path
              d="M14 8a6 6 0 00-6-6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          {/* Pulsing center dot */}
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{ animation: 'pulse-dot 1.5s ease-in-out infinite' }}
          >
            <div className="w-1.5 h-1.5 bg-blue-400 dark:bg-blue-500 rounded-full" />
          </div>
        </div>

        {/* Status text with fade transition */}
        <span
          key={currentStepIndex}
          className="text-sm font-medium animate-fade-in whitespace-nowrap"
        >
          {currentStep}
        </span>

        {/* Progress dots showing which step we're on */}
        {steps.length > 1 && (
          <div className="flex items-center gap-1 ml-1">
            {steps.map((_, index) => (
              <div
                key={index}
                className={cn(
                  "w-1 h-1 rounded-full transition-all duration-300",
                  index === currentStepIndex
                    ? "bg-white dark:bg-gray-900 w-2"
                    : index < currentStepIndex
                      ? "bg-white/60 dark:bg-gray-900/60"
                      : "bg-white/30 dark:bg-gray-900/30"
                )}
              />
            ))}
          </div>
        )}
      </div>

      {/* Inline styles for animations */}
      <style>{`
        @keyframes pulse-dot {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.5;
            transform: scale(0.8);
          }
        }

        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(-4px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.25s ease-out;
        }
      `}</style>
    </div>
  );
}
